import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { motion } from 'motion/react'

type LoadingBarProps = {
    // Progress the bar animates towards over `duration`
    progress: number
    duration: number
    className?: string
}

// `atProgress` places the icon on the track. Collection fires a few points earlier
// so the icon is already vanishing as the character runs over it
const COLLECT_EARLY_BY_PROGRESS = 5

const loaderIcons = [
    { key: 'elephant', src: '/images/loadingBar/LoaderElephant.png', atProgress: 20 },
    { key: 'pen', src: '/images/loadingBar/LoaderPen.png', atProgress: 40 },
    { key: 'cup', src: '/images/loadingBar/LoaderCup.png', atProgress: 60 },
    { key: 'ufo', src: '/images/loadingBar/LoaderUfo.png', atProgress: 80 },
] as const

export default function LoadingBar({ progress, duration, className }: LoadingBarProps) {
    const trackRef = useRef<HTMLDivElement | null>(null)
    const fillRef = useRef<HTMLDivElement | null>(null)
    const markerRef = useRef<HTMLDivElement | null>(null)
    // Distance of each icon center from the left edge of the track, in px
    const iconOffsetsRef = useRef<number[]>([])
    const collectedCountRef = useRef(0)
    const [collectedIconKeys, setCollectedIconKeys] = useState<readonly string[]>([])
    const [trackWidth, setTrackWidth] = useState(0)
    const fillAnimationRef = useRef<Animation | null>(null)
    const markerAnimationRef = useRef<Animation | null>(null)
    const previousDurationRef = useRef(Math.max(duration, 0))
    const previousFillTransformRef = useRef<string | null>(null)
    const previousMarkerTransformRef = useRef<string | null>(null)

    useEffect(() => {
        const track = trackRef.current

        if (!track) return

        const measure = () => {
            const trackRect = track.getBoundingClientRect()

            if (!trackRect.width) return

            setTrackWidth(trackRect.width)

            // Same coordinate space as the marker transform: px from the left
            // edge of the track. Collect a little before the icon's center
            iconOffsetsRef.current = loaderIcons.map(
                icon => (trackRect.width * (icon.atProgress - COLLECT_EARLY_BY_PROGRESS)) / 100,
            )
        }

        measure()

        const resizeObserver = new ResizeObserver(measure)
        resizeObserver.observe(track)

        window.addEventListener('resize', measure)

        return () => {
            resizeObserver.disconnect()
            window.removeEventListener('resize', measure)
        }
    }, [])

    // The marker is animated on the compositor, so the only reliable source for
    // "has the character passed this icon" is the transform that is on screen.
    // Anything derived from the progress prop drifts away from it under load
    useEffect(() => {
        let frameId = 0

        const sample = () => {
            const marker = markerRef.current
            const offsets = iconOffsetsRef.current

            if (marker && offsets.length) {
                const transform = getComputedStyle(marker).transform
                const markerX = transform === 'none' ? 0 : new DOMMatrixReadOnly(transform).m41

                let count = 0
                while (count < offsets.length && markerX >= offsets[count]) {
                    count += 1
                }

                // Collected icons are animating out, so re-measuring must never
                // bring one back
                if (count > collectedCountRef.current) {
                    collectedCountRef.current = count
                    setCollectedIconKeys(loaderIcons.slice(0, count).map(icon => icon.key))
                }

                if (count >= loaderIcons.length) return
            }

            frameId = window.requestAnimationFrame(sample)
        }

        frameId = window.requestAnimationFrame(sample)
        return () => window.cancelAnimationFrame(frameId)
    }, [])

    const clampedProgress = Math.max(0, Math.min(100, progress))
    const fillTransform = `translate(${((clampedProgress - 100) * trackWidth) / 100}px, 0px)`
    const markerTransform = `translate(${(trackWidth * clampedProgress) / 100}px, -50%)`

    useEffect(() => {
        // Before the track is measured every progress maps onto the same transform,
        // which would show the bar as full and then let it slide backwards
        if (!trackWidth) return

        const animateTransform = (
            element: HTMLDivElement | null,
            animationRef: { current: Animation | null },
            targetTransform: string,
            fallbackTransform: string,
            durationMs: number,
        ) => {
            if (!element) return

            const currentTransform = getComputedStyle(element).transform
            const fromTransform = currentTransform === 'none' ? fallbackTransform : currentTransform

            animationRef.current?.cancel()

            if (durationMs <= 0 || fromTransform === targetTransform) {
                animationRef.current = null
                element.style.transform = targetTransform
                return
            }

            element.style.transform = fromTransform
            animationRef.current = element.animate(
                [
                    { transform: fromTransform },
                    { transform: targetTransform },
                ],
                {
                    duration: durationMs,
                    easing: 'linear',
                    fill: 'forwards',
                },
            )
        }

        const nextDuration = Math.max(duration, 0)
        const fillChanged = previousFillTransformRef.current !== fillTransform
        const markerChanged = previousMarkerTransformRef.current !== markerTransform

        if (fillChanged) {
            animateTransform(fillRef.current, fillAnimationRef, fillTransform, `translate(${-trackWidth}px, 0px)`, nextDuration)
            previousFillTransformRef.current = fillTransform
        }

        if (markerChanged) {
            animateTransform(markerRef.current, markerAnimationRef, markerTransform, 'translate(0px, -50%)', nextDuration)
            previousMarkerTransformRef.current = markerTransform
        }

        const previousDuration = previousDurationRef.current
        if (!fillChanged && !markerChanged && nextDuration > 0 && previousDuration > 0 && previousDuration !== nextDuration) {
            const playbackRate = previousDuration / nextDuration

            if (fillAnimationRef.current) {
                fillAnimationRef.current.playbackRate *= playbackRate
            }

            if (markerAnimationRef.current) {
                markerAnimationRef.current.playbackRate *= playbackRate
            }
        }

        previousDurationRef.current = nextDuration
    }, [duration, fillTransform, markerTransform, trackWidth])

    useEffect(() => () => {
        fillAnimationRef.current?.cancel()
        markerAnimationRef.current?.cancel()
    }, [])

    return (
        <div
            className={clsx(
                'flex justify-center items-center bg-white-gradient rounded-full w-full',
                'drop-shadow-2-white-stroke p-0.5',
                'stele:drop-shadow-2-white-stroke stele:p-2',
                className,
            )}
        >
            <div
                ref={trackRef}
                className={clsx(
                    'relative flex justify-center items-center bg-orange-stroke rounded-full w-full h-full',
                    'p-0.5 min-h-5 max-h-5',
                    'stele:p-2 stele:min-h-20 stele:max-h-20'
                )}
            >
                <div className='relative bg-orange rounded-full w-full h-full overflow-hidden' >
                    <div
                        ref={fillRef}
                        className='top-0 left-0 absolute bg-orange-gradient rounded-full w-full h-full will-change-transform'
                    />
                    <div className='top-px right-1 left-1 z-20 absolute bg-shine-gradient rounded-full h-3' />
                </div>
                <div
                    ref={markerRef}
                    className='top-1/2 left-0 z-50 absolute will-change-transform'
                >
                    <img
                        src='/images/ChameleRun.gif'
                        className={clsx(
                            '-translate-x-1/2',
                            'w-12 h-12',
                            'min-w-12 min-h-12',
                            'stele:w-48 stele:h-48',
                            'stele:min-w-48 stele:min-h-48'
                        )}
                    />
                </div>
                {loaderIcons.map((icon) => {
                    const isCollected = collectedIconKeys.includes(icon.key)

                    return (
                        // The wrapper owns the placement so `motion` stays free to
                        // write the transform of the image
                        <div
                            key={icon.key}
                            className='top-1/2 z-40 absolute -translate-x-1/2 -translate-y-1/2'
                            style={{ left: `${icon.atProgress}%` }}
                        >
                            <motion.img
                                src={icon.src}
                                initial={false}
                                animate={isCollected
                                    ? {
                                        scale: [1, 1.25, 0],
                                        y: [0, -6, -14],
                                        opacity: [1, 1, 0],
                                    }
                                    : {
                                        scale: 1,
                                        y: 0,
                                        opacity: 1,
                                    }}
                                transition={isCollected
                                    ? {
                                        duration: 0.45,
                                        times: [0, 0.45, 1],
                                        ease: 'easeOut',
                                    }
                                    : {
                                        duration: 0.2,
                                        ease: 'easeOut',
                                    }}
                                className={clsx(
                                    'origin-center shrink-0',
                                    'w-11 h-11',
                                    'stele:w-44 stele:h-44'
                                )}
                            />
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
